import { supabasePlugin } from "@/plugins/supabase.ts";
import exception from "@/utils/exception";
import Constants from "src/utils/constants";

let _tagTable = Constants.enums.TABLES_NAME_ENUM.TAGS_TABLE;

// Function to insert data into a table
async function insertData(tableName, obj) {
  try {
    const { data, error, status } = await supabasePlugin.client
      .from(tableName)
      .insert(obj)
      .select();
    return {
      data: data && data.length > 0 ? data[0] : null,
      error: (error && error.message) || null,
      status
    };
  } catch (error) {
    // Assuming exception.catchException is a function you've defined to handle exceptions
    exception.catchException(error);

    return {
      data: null,
      error: error.message || "An unexpected error occurred",
      status: null
    };
  }
}

// Function to update data in a table
async function updateData(tableName, obj) {
  try {
    const { error, status } = await supabasePlugin.client
      .from(tableName)
      .update(obj)
      .eq("id", obj.id);

    return {
      error: (error && error.message) || null
    };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

// Function to get data from a table by ID
async function getData(tableName, id) {
  try {
    const { data, error, status } = await supabasePlugin.client
      .from(tableName)
      .select()
      .eq("id", id)
      .single(); // Ensure only one record is returned

    if (error) {
      return {
        error: error.message || null,
        data: null
      };
    }

    data.form_schema = JSON.parse(data.form_schema);
    data.grid_schema_data = JSON.parse(data.grid_schema_data);

    return {
      error: null,
      data
    };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}
// Function to get data from a table by Tag
async function getDataByTag(tableName, tag) {
  try {
    const { data, error, status } = await supabasePlugin.client
      .from(tableName)
      .select(
        `*,
      ${_tagTable}!inner(tag)
    `
      )
      .eq(`${_tagTable}.tag`, tag)
      .single();

    if (error) {
      return {
        error: error.message || null,
        data: null
      };
    }

    return {
      error: null,
      data: {
        schema: JSON.parse(data.form_schema),
        table_name: data.table_name
      }
    };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

async function getDataBySlug(tableName, slug) {
  try {
    const { data, error, status } = await supabasePlugin.client
      .from(tableName)
      .select()
      .eq(`url_slug`, slug)
      .single();

    if (error) {
      return {
        error: error.message || null,
        data: null
      };
    }

    data.form_schema = JSON.parse(data.form_schema);
    data.grid_schema_data = JSON.parse(data.grid_schema_data);

    return {
      error: null,
      data
    };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

// Function to fetch data from a table with optional filtering, sorting, and pagination
async function fetchData(
  tableName,
  fetchDeleted = false,
  searchTxt = null,
  sortField = null,
  sortOrder = 0, // 0 = none, 1 = asc, -1 = desc
  page = 0,
  pageSize = 10
) {
  try {
    // _tagTable

    // Initial query setup
    let query = supabasePlugin.client
      .from(tableName)
      .select(
        `id, created_at, updated_at, tag, notes, url_slug, is_deleted, tag_obj:${_tagTable} (id, tag, notes)`,
        { count: "exact" }
      );

    // Apply filter for deleted items
    if (!fetchDeleted) {
      query = query.eq("is_deleted", false);
    }

    // Apply search text to all columns if provided
    if (searchTxt) {
      let _res = await getColumnNames(tableName);
      const searchableColumns = _res.data || [];
      const searchFilters = searchableColumns
        .map((col) => `${col}.ilike.%${searchTxt}%`)
        .join(",");
      query = query.or(searchFilters);
    }

    // Get the total count of filtered data
    const { count, error: countError } = await query;
    if (countError) {
      return {
        error: (countError && countError.message) || null
      };
    }

    // Apply sorting
    if (!sortField) {
      query = query.order("created_at", {
        ascending: sortOrder === 1
      });
    } else {
      query = query.order(sortField, {
        ascending: sortOrder === 1
      });
    }

    // Apply pagination
    const from = page * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    // Execute the query
    const { data, error } = await query;

    return {
      data,
      totalItems: count,
      error: (error && error.message) || null
    };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

// Function to fetch column names from a table using a supabasePlugin.client RPC function
async function getColumnNames(table_name) {
  try {
    const { data, error } = await supabasePlugin.client.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_COLUMN_NAMES,
      { p_table_name: table_name }
    );

    return { data, error: (error && error.message) || null };
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function checkAndUpdateTable(table_name, col_list) {
  try {
    const { data, error } = await supabasePlugin.client.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.CHECK_AND_CREATE_TABLE,
      {
        p_table_name: table_name,
        p_column_names: col_list
      }
    );
    const { data: _data, error: _error } = await supabasePlugin.client.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.SETUP_RLS_FOR_AUTHENTICATED_USERS,
      {
        p_table_name: table_name
      }
    );

    // return { data, error: (error && error.message) || null };
  } catch (error) {
    console.error(error);
    return [];
  }
}

export default {
  insertData,
  fetchData,
  updateData,
  getData,
  getDataByTag,
  getDataBySlug,
  checkAndUpdateTable
};
